import * as React from 'react';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import DataPoolActions from './DataPoolActions';
import ErrorMsg from '../common/ErrorMsg';
import InDevelopment from '../common/InDevelopment';
import PaginationNav from '../common/PaginationNav';
import { IDataPool } from '../../types/my-types';
import { standardTableStyles } from '../common/tableStyles';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { LinkTableStyled } from '../common/StyledComponents';

interface OrgDataPoolsProps {
  data: {
    results: IDataPool[];
    count: number;
  } | undefined;
  isLoading: boolean;
  isError: boolean;
  total: number;
  page: number;
  previousPage: () => void;
  nextPage: () => void;
}

function OrgDataPools({
  data,
  isLoading,
  isError,
  total,
  page,
  previousPage,
  nextPage,
}: OrgDataPoolsProps) {
  const PAGE_LIMIT = 100;

  /* reduce query since it only has one active core
	const glynt = useGlynt();
	const { data: core, isLoading: loading, isError: error } = useQuery(
		"core",
		async () => (await glynt).org.core()
	);
	let { active, experimental, deprecated: deprecatedVersions } =
		core?.data;
	let coreVersions = core ? [...active, ...experimental] : [];
  */

  if (isError) {
    return <ErrorMsg error="Sorry, failed to load datapools." />;
  }

  const dataPools: IDataPool[] = (data?.results || []).slice().sort((a, b) => {
    // Sort ACTIVE first, then by label alphabetically
    if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
    if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
    return a.label.localeCompare(b.label);
  });
  const dataPoolNames = dataPools.map((dp) => dp.label);

  return (
    <div id="org-data-pools" style={{ width: '100%', overflowX: 'auto' }}>
      <Table sx={standardTableStyles.tableSx}>
        <TableHead sx={standardTableStyles.tableHeadSx}>
          <TableRow>
            <TableCell>Data Pool Name</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Use</TableCell>
            <InDevelopment>
              <TableCell>Core</TableCell>
            </InDevelopment>
            <TableCell align="center">Vendors</TableCell>
            <TableCell>Validate</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={standardTableStyles.tableBodySx}>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={`${50 + (i * 23) % 40}%`} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={72} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={22} width={48} sx={{ borderRadius: '6px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={22} width={56} sx={{ borderRadius: '6px' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={72} sx={{ borderRadius: '4px' }} /></TableCell>
                  <InDevelopment><TableCell><Skeleton animation="wave" variant="rounded" height={14} width={40} sx={{ borderRadius: '4px' }} /></TableCell></InDevelopment>
                  <TableCell align="center"><Skeleton animation="wave" variant="circular" width={18} height={18} sx={{ mx: 'auto' }} /></TableCell>
                  <TableCell><Skeleton animation="wave" variant="rounded" height={22} width={64} sx={{ borderRadius: '4px' }} /></TableCell>
                  <TableCell align="right"><Skeleton animation="wave" variant="rounded" height={14} width={24} sx={{ borderRadius: '4px', ml: 'auto' }} /></TableCell>
                </TableRow>
              ))
            : null}
          {!isLoading && dataPools.map((dataPool: IDataPool) => (
            <TableRow
              key={dataPool.id}
              sx={dataPool.status !== 'ACTIVE' ? { opacity: 0.55 } : undefined}
            >
              <TableCell data-cy="link-data-pool">
                <LinkTableStyled
                  to={`/data-pools/${dataPool.id}/extraction-batches`}
                  data-cy={`link-data-pool-${dataPool.label}`}
                >
                  {dataPool.label}
                </LinkTableStyled>
              </TableCell>
              <TableCell data-cy={`tbl-cell-datapool-client-${dataPool.label}`} sx={{ color: '#5f6d7e' }}>
                {dataPool.client?.name || <span>&mdash;</span>}
              </TableCell>
              <TableCell data-cy={`tbl-cell-datapool-priority-${dataPool.label}`}>
                {dataPool.priority === 'High' ? (
                  <Chip
                    label="HIGH"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(244,67,54,0.08)',
                      color: '#F44336',
                      fontWeight: 600,
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      borderRadius: '6px',
                    }}
                  />
                ) : (
                  <span style={{ color: '#5f6d7e' }}>&mdash;</span>
                )}
              </TableCell>
              <TableCell data-cy={`tbl-cell-datapool-status--${dataPool.label}`}>
                <Chip
                  label={dataPool.status}
                  size="small"
                  data-cy={`chip-datapool-status-${dataPool.label}-${dataPool.status}`}
                  sx={
                    dataPool.status === 'ACTIVE'
                      ? {
                          bgcolor: 'rgba(46,184,144,0.22)',
                          color: '#0d7a5f',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          borderRadius: '6px',
                        }
                      : {
                          bgcolor: 'rgba(95,109,126,0.12)',
                          color: '#8a95a3',
                          fontWeight: 500,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          borderRadius: '6px',
                          border: '1px dashed rgba(95,109,126,0.3)',
                        }
                  }
                />
              </TableCell>
              <TableCell
                data-cy={`tbl-cell-datapool-use-${dataPool.label}`}
                sx={{ color: '#5f6d7e' }}
              >
                {dataPool.use}
              </TableCell>
              <InDevelopment>
                <TableCell>4.0.0</TableCell>
              </InDevelopment>
              <TableCell
                align="center"
                data-cy={`table-suggest-vendors-${dataPool.label}`}
              >
                {dataPool.config?.suggest_new_vendors ? (
                  <CheckCircleOutlinedIcon sx={{ color: '#66BBA9', fontSize: 18 }} />
                ) : (
                  <span style={{ color: '#5f6d7e' }}>&mdash;</span>
                )}
              </TableCell>
              <TableCell data-cy={`tbl-cell-validation-datasets-${dataPool.label}`}>
                {dataPool.config?.validation_datasets &&
                dataPool.config.validation_datasets.length > 0 ? (
                  dataPool.config.validation_datasets.map((dataset) => (
                    <Chip
                      key={dataset}
                      label={dataset.charAt(0).toUpperCase() + dataset.slice(1)}
                      size="small"
                      variant="outlined"
                      sx={{
                        mr: 0.5,
                        mb: 0.5,
                        '&:last-child': { mb: 0 },
                        fontWeight: 600,
                        fontSize: '10.5px',
                        height: 22,
                        borderRadius: '4px',
                        ...(dataset === 'extracted' && {
                          bgcolor: 'rgba(29,154,170,0.1)',
                          color: '#1D9AAA',
                          borderColor: 'rgba(29,154,170,0.2)',
                        }),
                        ...(dataset === 'transformed' && {
                          bgcolor: 'rgba(255,197,63,0.12)',
                          color: '#b8860b',
                          borderColor: 'rgba(255,197,63,0.25)',
                        }),
                        ...(dataset === 'schematized' && {
                          bgcolor: 'rgba(102,187,169,0.12)',
                          color: '#1a8a6e',
                          borderColor: 'rgba(102,187,169,0.25)',
                        }),
                      }}
                      data-cy={`chip-validation-dataset-${dataPool.label}-${dataset}`}
                    />
                  ))
                ) : (
                  <span style={{ color: '#5f6d7e' }}>&mdash;</span>
                )}
              </TableCell>
              <TableCell align="right">
                <DataPoolActions dataPool={dataPool} dataPoolNames={dataPoolNames} />
              </TableCell>
            </TableRow>
          ))}
          {!isLoading && dataPools.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#5f6d7e' }}>
                No data pools found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {data && data.count > PAGE_LIMIT ? (
        <PaginationNav
          total={total}
          page={page}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      ) : null}
    </div>
  );
}

export default OrgDataPools;
